import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";

interface VendorModuleRow {
  key: string;
  label: string;
  is_generic: boolean;
}

interface VendorUserRow {
  userId: string;
  email: string | null;
  displayName: string | null;
  disabledAt: string | null;
  inviteAcceptedAt: string | null;
  createdAt: string;
}

function vendorUserStatus(row: VendorUserRow): "disabled" | "active" | "invite_pending" {
  if (row.disabledAt != null) {
    return "disabled";
  }
  if (row.inviteAcceptedAt == null) {
    return "invite_pending";
  }
  return "active";
}

function vendorUserStatusLabel(status: ReturnType<typeof vendorUserStatus>): JSX.Element {
  if (status === "disabled") {
    return <span className="text-slate-500">Disabled</span>;
  }
  if (status === "invite_pending") {
    return <span className="text-amber-700">Invite pending</span>;
  }
  return <span className="text-green-700">Active</span>;
}

interface SupplierModuleRow {
  key: string;
  label: string;
  enabled: boolean;
}

interface PortalSupplierRow {
  mvSupplierId: number;
  mvSupplierName: string;
}

const PAGE_HOVER_HINT =
  "Invite vendor users by email (magic link), list portal accounts per Megaventory supplier id, and enable supplier-specific modules. The supplier list includes only Megaventory suppliers that already have portal users or enabled supplier-specific modules (for example 1467, Specialty Coating Systems potting vendor).";

export function VendorPortalPage() {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const isAdmin = useMemo(() => isIntegratorAdmin(meQuery.data?.user), [meQuery.data?.user]);

  const [mvSupplierId, setMvSupplierId] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDisplayName, setInviteDisplayName] = useState("");

  const portalSuppliersQuery = useQuery({
    queryKey: ["user-admin", "vendor-portal-suppliers"],
    queryFn: () =>
      apiFetch<{ suppliers: PortalSupplierRow[] }>("/api/user-admin/vendor-portal-suppliers"),
    enabled: isAdmin,
  });

  const portalSuppliers = portalSuppliersQuery.data?.suppliers ?? [];

  useEffect(() => {
    if (portalSuppliers.length === 0) {
      return;
    }
    if (mvSupplierId != null && portalSuppliers.some((row) => row.mvSupplierId === mvSupplierId)) {
      return;
    }
    const preferred = portalSuppliers.find((row) => row.mvSupplierId === 1467);
    setMvSupplierId(preferred?.mvSupplierId ?? portalSuppliers[0].mvSupplierId);
  }, [portalSuppliers, mvSupplierId]);

  const mvParsed = mvSupplierId ?? NaN;

  const modulesQuery = useQuery({
    queryKey: ["user-admin", "vendor-modules"],
    queryFn: () => apiFetch<{ modules: VendorModuleRow[] }>("/api/user-admin/vendor-modules"),
    enabled: isAdmin,
  });

  const usersQuery = useQuery({
    queryKey: ["user-admin", "vendor-users", mvParsed],
    queryFn: () =>
      apiFetch<{ users: VendorUserRow[] }>("/api/user-admin/vendor-users", {
        query: { mvSupplierId: mvParsed },
      }),
    enabled: isAdmin && Number.isFinite(mvParsed) && mvParsed > 0,
  });

  const supplierModulesQuery = useQuery({
    queryKey: ["user-admin", "vendor-supplier-modules", mvParsed],
    queryFn: () =>
      apiFetch<{ modules: SupplierModuleRow[] }>("/api/user-admin/vendor-supplier-modules", {
        query: { mvSupplierId: mvParsed },
      }),
    enabled: isAdmin && Number.isFinite(mvParsed) && mvParsed > 0,
  });

  const inviteMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ userId: string }>("/api/user-admin/vendor-users", {
        method: "POST",
        body: {
          email: inviteEmail.trim(),
          mvSupplierId: mvParsed,
          displayName: inviteDisplayName.trim() === "" ? null : inviteDisplayName.trim(),
        },
      }),
    onSuccess: () => {
      setInviteEmail("");
      setInviteDisplayName("");
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "vendor-users", mvParsed] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/api/user-admin/vendor-users/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        body: { disabled: true },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "vendor-users", mvParsed] });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ email: string }>(
        `/api/user-admin/vendor-users/${encodeURIComponent(userId)}/resend-invite`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "vendor-users", mvParsed] });
    },
  });

  const toggleModuleMutation = useMutation({
    mutationFn: (input: { moduleKey: string; enabled: boolean }) =>
      apiFetch("/api/user-admin/vendor-supplier-modules", {
        method: "POST",
        body: { mvSupplierId: mvParsed, moduleKey: input.moduleKey, enabled: input.enabled },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "vendor-supplier-modules", mvParsed] });
    },
  });

  const vendorUsers = usersQuery.data?.users ?? [];

  const vendorUserColumns = useMemo((): HubDataTableColumn<VendorUserRow>[] => {
    return [
      {
        id: "email",
        header: "Email",
        sortValue: (row) => row.email ?? "",
        cell: (row) => row.email ?? "—",
      },
      {
        id: "displayName",
        header: "Display name",
        sortValue: (row) => row.displayName ?? "",
        cell: (row) => row.displayName ?? "—",
      },
      {
        id: "status",
        header: "Status",
        sortValue: (row) => vendorUserStatus(row),
        cell: (row) => vendorUserStatusLabel(vendorUserStatus(row)),
      },
      {
        id: "createdAt",
        header: "Created",
        overflow: "nowrap",
        sortValue: (row) => row.createdAt,
        cell: (row) => row.createdAt,
      },
      {
        id: "actions",
        header: "Actions",
        sortValue: () => "",
        cell: (row) => {
          const status = vendorUserStatus(row);
          const resendPending =
            resendInviteMutation.isPending && resendInviteMutation.variables === row.userId;
          return (
            <div className="flex flex-wrap items-center gap-2">
              {status === "invite_pending" ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={resendInviteMutation.isPending}
                  onClick={() => {
                    resendInviteMutation.mutate(row.userId);
                  }}
                >
                  {resendPending ? "Sending…" : "Resend invite"}
                </Button>
              ) : null}
              {status !== "disabled" ? (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  disabled={disableMutation.isPending}
                  onClick={() => {
                    if (window.confirm("Disable this vendor user and ban their Auth login?")) {
                      disableMutation.mutate(row.userId);
                    }
                  }}
                >
                  Disable
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [disableMutation, resendInviteMutation]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-xl font-semibold text-slate-900">Vendor portal</h1>
        <p className="mt-2 text-sm text-slate-600">Only integrator administrators can manage vendor portal users.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT} titleClassName="text-2xl">
          Vendor portal
        </PageTitleWithHelp>
        {portalSuppliersQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading suppliers…</p>
        ) : portalSuppliersQuery.isError ? (
          <p className="text-sm text-red-600">
            {portalSuppliersQuery.error instanceof Error
              ? portalSuppliersQuery.error.message
              : "Failed to load portal suppliers"}
          </p>
        ) : portalSuppliers.length === 0 ? (
          <p className="text-sm text-slate-600">No portal suppliers yet.</p>
        ) : (
          <select
            id="mv-supplier-id"
            aria-label="Megaventory supplier"
            value={mvSupplierId ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              setMvSupplierId(value === "" ? null : Number.parseInt(value, 10));
            }}
            className={cn(
              "h-9 max-w-md rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900",
              "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
            )}
          >
            {portalSuppliers.map((row) => (
              <option key={row.mvSupplierId} value={row.mvSupplierId}>
                {row.mvSupplierName} ({row.mvSupplierId})
              </option>
            ))}
          </select>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Invite vendor user</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              className="mt-1 max-w-md"
            />
          </div>
          <div>
            <Label htmlFor="invite-display">Display name (optional)</Label>
            <Input
              id="invite-display"
              value={inviteDisplayName}
              onChange={(event) => setInviteDisplayName(event.target.value)}
              className="mt-1 max-w-md"
            />
          </div>
          <Button
            type="button"
            disabled={
              inviteMutation.isPending ||
              !Number.isFinite(mvParsed) ||
              mvParsed <= 0 ||
              inviteEmail.trim() === ""
            }
            onClick={() => {
              inviteMutation.mutate();
            }}
          >
            {inviteMutation.isPending ? "Sending…" : "Send magic link invite"}
          </Button>
          {inviteMutation.isError ? (
            <p className="text-sm text-red-600">
              {inviteMutation.error instanceof Error ? inviteMutation.error.message : "Invite failed"}
            </p>
          ) : null}
          {inviteMutation.isSuccess ? (
            <p className="text-sm text-green-700">Invite email sent via hub mail.</p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vendor users for this supplier</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <HubDataTable
            fillViewport={false}
            className="max-h-none border-0 shadow-none"
            columns={vendorUserColumns}
            rows={vendorUsers}
            rowKey={(row) => row.userId}
            loading={usersQuery.isLoading}
            loadingMessage="Loading…"
            error={
              usersQuery.isError
                ? usersQuery.error instanceof Error
                  ? usersQuery.error.message
                  : "Failed to load users"
                : null
            }
            empty="No vendor users for this supplier yet."
          />
          {resendInviteMutation.isError ? (
            <p className="mt-3 text-sm text-red-600">
              {resendInviteMutation.error instanceof Error
                ? resendInviteMutation.error.message
                : "Resend invite failed"}
            </p>
          ) : null}
          {resendInviteMutation.isSuccess ? (
            <p className="mt-3 text-sm text-green-700">
              Invite email resent to {resendInviteMutation.data?.email ?? "the vendor"}.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Supplier-specific modules</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {supplierModulesQuery.isLoading ? (
            <p className="text-sm text-slate-600">Loading…</p>
          ) : supplierModulesQuery.isError ? (
            <p className="text-sm text-red-600">
              {supplierModulesQuery.error instanceof Error
                ? supplierModulesQuery.error.message
                : "Failed to load modules"}
            </p>
          ) : (
            <ul className="space-y-2">
              {(supplierModulesQuery.data?.modules ?? []).map((mod) => (
                <li key={mod.key} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <div>
                    <p className="font-medium text-slate-900">{mod.label}</p>
                    <p className="text-xs text-slate-500">{mod.key}</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <span>Enabled</span>
                    <input
                      type="checkbox"
                      checked={mod.enabled}
                      disabled={toggleModuleMutation.isPending || !Number.isFinite(mvParsed) || mvParsed <= 0}
                      onChange={(event) => {
                        toggleModuleMutation.mutate({ moduleKey: mod.key, enabled: event.target.checked });
                      }}
                    />
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div>
            <p className="text-xs font-medium text-slate-600">All registered module keys</p>
            <ul className="mt-1 list-inside list-disc text-xs text-slate-500">
              {(modulesQuery.data?.modules ?? []).map((m) => (
                <li key={m.key}>
                  {m.key} — {m.label} {m.is_generic ? "(generic)" : ""}
                </li>
              ))}
            </ul>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
